import { gsap } from "gsap";
import { Sprite, Texture } from "pixi.js";

import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { fitDisplayObjectToWidth } from "../../../../abstracts/utils/GraphicUtils";
import { getRandomElement } from "../../../../abstracts/utils/JSUtils";
import { playSound } from "../../../../abstracts/utils/SoundUtils";
import {
    ApocalypseFontsNames,
    m3DoubleValueSounds,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { getWinLevel, lowSymbolFsValues, symbolConfig } from "../../../config/ApocalypseConfig";
import {
    ApocalypseHighSymbolNames,
    ApocalypseLowSymbolNames,
    ApocalypseSymbolNames,
    ApocalypseSymbolSize,
} from "../../../types/ApocalypseEnums";
import { ApocalypseCounter } from "../../../utils/ApocalypseCounter";
import { getBitmapText, returnBitmapText } from "./ApocalypseBitmapTextPool";

export class ApocalypseSymbol extends AbstractSymbol {
    protected mainTextureDefaultXPosition: number;
    protected mainTextureDefaultYPosition: number;
    protected mainSpineDefaultXPosition: number;
    protected mainSpineDefaultYPosition: number;
    protected mainTextureDefaultName: string;

    private counterYPosition: number = 74;
    private bigCounterHighlightTl: gsap.core.Timeline;
    private bigCounterHighlightTween: gsap.core.Tween;
    private bigCounterClone: AbstractBitmapText | null = null;

    public counter: ApocalypseCounter;

    constructor(symbolName: string) {
        super(symbolName, ApocalypseSymbolSize.X1);
    }

    protected get symbolHeight(): number {
        return symbolConfig.symbolHeight;
    }

    set size(value: number | ApocalypseSymbolSize) {
        this._size = value;
    }

    get size() {
        return this._size;
    }

    set isSpinning(value: boolean) {
        this._isSpinning = value;

        if (value && !this.isActivated) {
            const texture = Texture.from(this.getSpinTextureName());

            this.mainTextureSprite.texture = texture;
            this.mainTextureSprite.y =
                symbolConfig.symbolHeight / 2 - this.mainTextureSprite.height / 2;
            this.addChildAt(this.mainTextureSprite, 0);
            this.removeChild(this.mainSpine);

            if (this.counter && this.counter.parent === this) {
                this.removeChild(this.counter);
            }
        }
    }

    protected getSpinTextureName(): string {
        if (
            this.counter &&
            this.counter.parent === this &&
            Object.values(ApocalypseLowSymbolNames).includes(this.name as ApocalypseLowSymbolNames)
        ) {
            return `${this.mainTextureDefaultName}_spin_fs`;
        }
        return `${this.mainTextureDefaultName}_spin`;
    }

    protected init(): void {
        if (this.name !== ApocalypseSymbolNames.BLANK && this.name !== ApocalypseSymbolNames.DUMP) {
            this.valueObj = {
                value: lowSymbolFsValues[this.name as ApocalypseLowSymbolNames]
                    ? lowSymbolFsValues[this.name as ApocalypseLowSymbolNames]
                    : 0,
            };

            this.counter = new ApocalypseCounter({
                initialValue: this.valueObj.value,
                fontProps: {
                    text: ``,
                    style: {
                        fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                        fontSize: 6,
                    },
                },
                onCounterStart: () => {
                    this.bigCounterHighlightTween?.kill();
                    this.bigCounterHighlightTl?.kill();
                    if (this.bigCounterClone !== null) {
                        returnBitmapText(this.bigCounterClone);
                        this.bigCounterClone = null;
                    }

                    this.counter.y = this.counterYPosition;
                    this.addChild(this.counter);
                },
                onCounterUpdate: () => {
                    this.alignCounter();
                },
                onCounterComplete: () => {
                    this.valueObj.value = this.counter.valueObj.value;

                    this.alignCounter();

                    if (this.valueObj.value > 100) {
                        this.startBigCounterHighlight(this.valueObj.value);
                    }
                },
                name: `valueCounter`,
                prefix: this.name === ApocalypseSymbolNames.M2 ? `+` : `x`,
            });

            this.counterYPosition = 74;
            this.counter.y = this.counterYPosition;

            this.alignCounter();
        }

        this.generateSymbol();
    }

    public alignCounter(): void {
        fitDisplayObjectToWidth(this.counter, 73);
        this.counter.x = symbolConfig.symbolWidth / 2 + 1;
    }

    public getHighSymbolFeatureAnimation(featureNumber: number = 1): gsap.core.Timeline {
        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
        });

        if (this.name !== ApocalypseHighSymbolNames.M1) {
            if (this.name === ApocalypseHighSymbolNames.M4) {
                tl.add(
                    this.mainSpine.getSpineTween(`feature_start`, false, () => {
                        this.removeChild(this.mainTextureSprite);
                        this.addChild(this.mainSpine);
                    })
                );
                tl.add(this.mainSpine.getSpineTween(`feature_loop`, true));
            } else {
                tl.add(
                    this.mainSpine.getSpineTween(
                        `feature`,
                        false,
                        () => {
                            this.removeChild(this.mainTextureSprite);
                            this.addChild(this.mainSpine);
                        },
                        null,
                        () => {
                            this.removeChild(this.mainSpine);
                            this.addChild(this.mainTextureSprite);
                        }
                    )
                );
            }
        } else {
            tl.add(
                this.mainSpine.getSpineTween(
                    `feature_${featureNumber}`,
                    false,
                    () => {
                        this.removeChild(this.mainTextureSprite);
                        this.addChild(this.mainSpine);
                    },
                    null,
                    () => {
                        this.removeChild(this.mainSpine);
                        this.addChild(this.mainTextureSprite);
                    }
                )
            );
        }

        return tl;
    }

    public getUpdateMultiplierAnimation(
        finalValue: number,
        isInstant: boolean = false
    ): gsap.core.Timeline {
        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onUpdate: () => {
                this.alignCounter();
            },
            onComplete: () => {
                this.alignCounter();
            },
        });
        tl.add(
            this.counter.getUpdateCounterTextAnimation(
                finalValue,
                isInstant,
                false,
                getWinLevel(finalValue).animationDuration / 10
            )
        );
        return tl;
    }

    public getM3MultiplierAnimation(finalValue: number): gsap.core.Timeline {
        const maxFinalValue = finalValue > 20000 ? 20000 : finalValue;

        // Create a new sprite using the texture
        const firstValueClone = new AbstractBitmapText({
            text: `x${finalValue / 2}`,
            style: {
                fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                fontSize: 6,
            },
        });
        firstValueClone.name = `firstClonedValueSprite_${this.name}`;
        firstValueClone.width = this.counter.width;
        firstValueClone.height = this.counter.height;
        firstValueClone.x = this.counter.x;
        firstValueClone.y = this.counterYPosition;

        const secondValueClone = new AbstractBitmapText({
            text: `x${finalValue / 2}`,
            style: {
                fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                fontSize: 6,
            },
        });
        secondValueClone.name = `secondClonedValueSprite_${this.name}`;
        secondValueClone.width = firstValueClone.width;
        secondValueClone.height = firstValueClone.height;
        secondValueClone.x = firstValueClone.x;
        secondValueClone.y = firstValueClone.y;

        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            delay: 0.1,
            onStart: () => {
                this.bigCounterHighlightTl?.kill();
                this.bigCounterHighlightTween?.kill();
                if (this.bigCounterClone !== null) {
                    returnBitmapText(this.bigCounterClone);
                    this.bigCounterClone = null;
                }

                this.counter.alpha = 0;
                this.counter.y = this.counterYPosition;
                this.counter.getUpdateCounterTextAnimation(maxFinalValue, true, false);
                this.addChild(firstValueClone);
                this.addChild(secondValueClone);

                playSound({ name: getRandomElement(m3DoubleValueSounds) });
            },
            onUpdate: () => {
                this.alignCounter();
                if (this.bigCounterClone !== null) {
                    this.bigCounterClone.alpha = 0;
                }
            },
            onComplete: () => {
                this.alignCounter();

                if (this.bigCounterClone !== null) {
                    this.bigCounterClone.alpha = 1;
                }
            },
        });

        const moveFirstValue = gsap.to(firstValueClone, {
            y: this.counterYPosition - 12,
            duration: 0.3,
        });

        tl.add(moveFirstValue, 0);

        const moveSecondValue = gsap.to(secondValueClone, {
            y: this.counterYPosition + 12,
            duration: 0.3,
        });

        tl.add(moveSecondValue, 0);

        const resetPositionAndRemoveClones = gsap.to([firstValueClone, secondValueClone], {
            y: this.counterYPosition,
            duration: 0.3,
            alpha: 0,
            onComplete: () => {
                this.removeChild(firstValueClone);
                firstValueClone.destroy();
                this.removeChild(secondValueClone);
                secondValueClone.destroy();
            },
        });

        tl.add(resetPositionAndRemoveClones, `>`);

        const showCounter = gsap.to(this.counter, {
            alpha: 1,
            y: this.counterYPosition,
            duration: 0.4,
        });

        tl.add(showCounter, `>-=0.2`);

        return tl;
    }

    protected generateSymbol(): void {
        if (
            this.name === ApocalypseSymbolNames.M1 ||
            this.name === ApocalypseSymbolNames.M2 ||
            this.name === ApocalypseSymbolNames.M3 ||
            this.name === ApocalypseSymbolNames.M4 ||
            this.name === ApocalypseSymbolNames.M5 ||
            this.name === ApocalypseSymbolNames.L1 ||
            this.name === ApocalypseSymbolNames.L2 ||
            this.name === ApocalypseSymbolNames.L3 ||
            this.name === ApocalypseSymbolNames.L4 ||
            this.name === ApocalypseSymbolNames.L5
        ) {
            let spineName: string = `spine/Apocalypse/${this.name}.json`;

            this.mainSpine = new AbstractSpine(spineName);
            this.mainSpine.getSpineTween(`Normal`);

            this.mainSpineDefaultXPosition = symbolConfig.symbolWidth / 2;
            this.mainSpineDefaultYPosition = symbolConfig.symbolHeight / 2;

            this.mainTextureDefaultName = `Apocalypse/` + this.name;
            this.mainTextureSprite = new Sprite(Texture.from(this.mainTextureDefaultName));
            this.mainTextureSprite.name = `mainTextureSprite`;
            this.mainTextureSprite.scale.set(0.5);
            this.mainTextureSprite.alpha = 1;

            this.mainTextureDefaultXPosition =
                symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
            this.mainTextureDefaultYPosition =
                symbolConfig.symbolHeight - this.mainTextureSprite.height;

            this.addChild(this.mainTextureSprite);

            if (this.name === ApocalypseSymbolNames.M1) {
                this.mainTextureDefaultYPosition += 0.5;
                // this.mainTextureSprite.y = 0.5;
            } else if (this.name === ApocalypseSymbolNames.M2) {
                this.mainSpineDefaultXPosition -= 1;
                this.mainSpineDefaultYPosition -= 0.5;
            } else if (this.name === ApocalypseSymbolNames.M3) {
                this.mainTextureDefaultYPosition += 1.5;
            } else if (this.name === ApocalypseSymbolNames.M4) {
                this.mainTextureDefaultYPosition += 7;
            } else if (this.name === ApocalypseSymbolNames.M5) {
                this.mainTextureDefaultYPosition += 1;
                this.mainSpineDefaultYPosition -= 1;
            }

            this.mainSpine.x = this.mainSpineDefaultXPosition;
            this.mainSpine.y = this.mainSpineDefaultYPosition;

            this.mainTextureSprite.x = this.mainTextureDefaultXPosition;
            this.mainTextureSprite.y = this.mainTextureDefaultYPosition;
        }
    }

    public showFsText(): void {
        this.addChild(this.counter);
        if (this.bigCounterClone !== null) {
            this.addChild(this.bigCounterClone);
        }
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline({
            onStart: () => {
                if (this.mainSpine) {
                    // @ts-ignore
                    this.mainSpine.filters = null;

                    this.mainSpine.tint = 0xffffff;

                    this.addChild(this.mainSpine);

                    this.removeChild(this.mainTextureSprite);
                }
            },
        });

        let symbolAnimation: gsap.core.Tween = this.mainSpine.getSpineTween(`Win`);

        tl.add(symbolAnimation, 0);

        return tl;
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        let symbolAnimation = gsap.to(this.mainTextureSprite, {
            pixi: { tint: 0x555555 },
            duration: 0.1,
            onStart: () => {
                this.removeChild(this.mainSpine);
                this.addChild(this.mainTextureSprite);
            },
        });

        return symbolAnimation;
    }

    public override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return this.mainSpine.getSpineTween(`Landing`);
    }

    public getMainSpine(): AbstractSpine {
        return this.mainSpine;
    }

    public startBigCounterHighlight(value: number): void {
        let tintColor = 0x35e21e;

        if (value >= 500) {
            tintColor = 0xf4ed15;
        }
        if (value >= 1000) {
            tintColor = 0xfc603f;
        }

        this.bigCounterClone = getBitmapText();
        this.bigCounterClone.name = `counterClone`;
        this.bigCounterClone.updateValue(`x${value}`);
        this.bigCounterClone.x = this.counter.x;
        this.bigCounterClone.y = this.counter.y;
        this.bigCounterClone.width = this.counter.width;
        this.bigCounterClone.height = this.counter.height;

        let duration: number = 0.3;
        if (value >= 500) {
            duration = 0.25;
        }
        if (value >= 1000) {
            duration = 0.2;
        }

        this.bigCounterHighlightTl = gsap.timeline({
            smoothChildTiming: true,
        });

        this.bigCounterHighlightTl.add(
            gsap.to(this.counter, {
                y: this.counterYPosition - 5,
                yoyo: true,
                repeat: -1,
                duration: duration,
            }),
            0
        );

        const bigCounterHighlightTween = gsap.to(this.bigCounterClone, {
            pixi: {
                colorize: tintColor,
                colorizeAmount: 1,
            },
            yoyo: true,
            repeat: -1,
            duration: duration,
            onStart: () => {
                if (this.bigCounterClone !== null) {
                    this.addChild(this.bigCounterClone);
                }
            },
            onUpdate: () => {
                if (this.bigCounterClone !== null) {
                    this.bigCounterClone?.position.set(this.counter.x, this.counter.y);
                }
            },
        });

        this.bigCounterHighlightTl.add(bigCounterHighlightTween, 0);
    }

    public resetOnStartSpin(): void {
        // @ts-ignore
        this.filters = null;

        this.resetMainSpine(true);
        this.removeChild(this.mainSpine);
        this.mainSpine.tint = 0xffffff;

        if (this.mainTextureSprite) {
            this.addChild(this.mainTextureSprite);

            gsap.set(this.mainTextureSprite, {
                tint: 0xffffff,
                alpha: 1,
            });

            // @ts-ignore
            this.mainTextureSprite.filters = null;
        }

        this.size = this.initialSize;
        this.isWild = false;
        this.isBlocked = false;

        if (this.counter && this.counter.parent === this) {
            this.addChild(this.counter);
        }
        if (this.bigCounterClone !== null) {
            this.addChild(this.bigCounterClone);
        }
    }

    public override reset(): void {
        this.x = 0;
        // @ts-ignore
        this.filters = null;

        this.resetMainSpine();
        if (this.mainSpine !== undefined) {
            this.mainSpine.tint = 0xffffff;
            // @ts-ignore
            this.mainSpine.filters = null;
        }

        if (this.mainTextureSprite) {
            this.addChild(this.mainTextureSprite);

            gsap.set(this.mainTextureSprite, {
                tint: 0xffffff,
                alpha: 1,
            });

            // @ts-ignore
            this.mainTextureSprite.filters = null;

            this.mainTextureSprite.texture = Texture.from(this.mainTextureDefaultName);
            this.mainTextureSprite.x = this.mainTextureDefaultXPosition;
            this.mainTextureSprite.y = this.mainTextureDefaultYPosition;

            this.mainTextureSprite.mask = null;
        }

        this.alpha = 1;

        this.scale.set(1);

        this.size = 1; //this.initialSize;

        this.isWild = false;

        this.isBlocked = false;

        if (this.counter) {
            this.valueObj = {
                value: lowSymbolFsValues[this.name as ApocalypseLowSymbolNames]
                    ? lowSymbolFsValues[this.name as ApocalypseLowSymbolNames]
                    : 0,
            };
            this.counter.reset();
            this.counter.updateValue(this.valueObj.value.toString());
            this.alignCounter();
            this.removeChild(this.counter);
        }

        this.pivot.set(0);
        this.rotation = 0;

        this.bigCounterHighlightTl?.kill();
        this.bigCounterHighlightTween?.kill();
        if (this.bigCounterClone !== null) {
            returnBitmapText(this.bigCounterClone);
            this.bigCounterClone = null;
        }
    }

    protected override resetMainSpine(isSpin: boolean = false): void {
        if (this.mainSpine !== undefined) {
            this.mainSpine.reset();

            this.mainSpine.getSpineTween(`Normal`);

            if (!isSpin) {
                this.mainSpine.mask = null;
                this.mainSpine.scale.set(0.5);

                this.mainSpine.x = this.mainSpineDefaultXPosition;
                this.mainSpine.y = this.mainSpineDefaultYPosition;
            }
        }
    }
}
